import type { SelectOption } from '../../../components/inputs/selectShared';
import type { OrgUnitTreeNode } from './organizationTypes';

/**
 * A unit's lane is its **tier**, not its depth.
 *
 * The tier is `OrgUnitType.SortOrder` — the rank shown as "Rank" in the Tiers catalog,
 * where Division 10 sits above Department 20 above Section 30. Laying the chart out by it
 * rather than by depth is what makes a skipped level visible: a unit hanging off a Department
 * but typed three tiers down renders three lanes over, with the tier it skipped left empty on
 * that branch.
 *
 * A unit's parent and its tier are already independent in the model — nothing stops a Section
 * under a Section — so this needs no schema change. It only draws what the data already
 * allows.
 */
/**
 * A unit's lane is its **tier**, identified by id.
 *
 * The id, not the tier's order value: two tiers can share an order — one restored onto a
 * value a renumber already handed out — and matching on that filed every unit of the second
 * tier into the first tier's column.
 */
export const laneOf = (node: OrgUnitTreeNode) => node.orgUnitTypeId;


interface Tier {
  id: string;
  name: string;
  sortOrder: number;
  /** The server's own position for this tier. Never recounted here — see `tierOptions`. */
  level: number;
}

/**
 * The ladder as `<Select>` options: **ordered by level, and labelled 1, 2, 3, 4.**
 *
 * The level a reader sees is the tier's **position** in the ladder, not the number stored
 * against it. Stored order values are deliberately gapped — 10, 20, 30, so a tier can be
 * slotted between two later — which makes them meaningless to show: a picker reading
 * "20, 10, 60, 30" tells the reader nothing about which tier is above which, and it is worse
 * than no number at all. The position always counts 1..n, whatever the gaps behind it.
 *
 * **The position comes from the server, and is never recounted here.** This feed is the whole
 * ladder, so counting would work — but the tier table's level is computed server-side, and
 * one fact derived in two places is one that eventually disagrees with itself.
 *
 * **Ordered here, so the caller must pass `sort="none"`.** A picker left to sort itself puts
 * the options in alphabetical order, which scrambles a ladder — the whole point of a level is
 * that the list reads top to bottom.
 *
 * Shared, so every surface that picks from this ladder shows it identically: the unit form
 * and the move form cannot disagree about what a tier looks like or which order they come in.
 */
export function tierOptions(tiers: readonly Tier[]): SelectOption[] {
  return [...tiers]
    .sort((a, b) => a.level - b.level)
    .map((tier) => ({
      value: tier.id,
      label: tier.name,
      // Trailing: the level is how far down the ladder the tier sits, and the name is what
      // the reader is scanning for.
      right: tier.level,
    }));
}
